import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ReferralBanner } from '../components/ReferralBanner';
import {
  Compass,
  Check,
  ArrowRight,
  Loader2,
  Crown,
  Sparkles,
  Settings,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getPlans(monthlyPrice, annualPrice) {
  return [
    {
      id: 'free',
      name: 'Gratis',
      price: '$0',
      period: '',
      features: [
        '3 consultas al Asesor IA por mes',
        'Benchmark Salarial con vista previa (sin límite de consultas)',
        'Liquidación con vista previa (sin límite de cálculos)',
        'Evaluador de Oferta con vista previa (sin límite de evaluaciones)',
        '2 cálculos de Sueldo Neto incluidos',
      ],
      cta: 'Crear cuenta gratis',
      popular: false,
    },
    {
      id: 'pro_monthly',
      name: 'Pro Mensual',
      price: `$${monthlyPrice}`,
      period: 'MXN/mes',
      features: [
        'Asesor IA ilimitado con memoria contextual',
        'Benchmark Salarial ilimitado',
        'Calculadora de Liquidación ilimitada',
        'Evaluador de Ofertas ilimitado',
        'Calculadora de Sueldo Neto ilimitada',
        'Planeación de Retiro (AFORE, pensión, Meta Inversa)',
      ],
      cta: 'Actualizar a Pro',
      popular: true,
      badge: 'Más popular',
    },
    {
      id: 'pro_annual',
      name: 'Pro Anual',
      price: `$${annualPrice}`,
      period: 'MXN/año',
      features: [
        'Todo lo de Pro Mensual',
        'Ahorra vs mensual',
        'Acceso completo todo el año',
        'Calculadora de Sueldo Neto ilimitada',
        'Planeación de Retiro (AFORE, pensión, Meta Inversa)',
      ],
      cta: 'Actualizar a Pro Anual',
      popular: false,
      badge: 'Mejor valor',
    },
  ];
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [monthlyPrice, setMonthlyPrice] = useState(129);
  const [annualPrice, setAnnualPrice] = useState(990);

  const isPro = user?.plan === 'pro';

  useEffect(() => {
    // Fetch dynamic pricing
    const fetchPricing = async () => {
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/pricing/info`, { headers });
        if (res.ok) {
          const data = await res.json();
          setMonthlyPrice(data.monthly_price);
          setAnnualPrice(data.annual_price);
        }
      } catch {}
    };
    fetchPricing();
  }, [isAuthenticated]);

  const plans = getPlans(monthlyPrice, annualPrice);

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error('El pago no se completó. Puedes intentarlo de nuevo.');
    }
  }, [searchParams]);

  const handlePlanClick = async (planId) => {
    if (planId === 'free') {
      navigate('/register');
      return;
    }

    if (!isAuthenticated) {
      toast.info('Inicia sesión para actualizar tu plan');
      navigate('/login');
      return;
    }

    if (isPro) {
      toast.info('Ya tienes el plan Pro activo');
      return;
    }

    setLoadingPlan(planId);
    try {
      const originUrl = window.location.origin;
      const response = await authFetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin_url: originUrl }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al crear sesión de pago');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'Error al procesar el pago');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const response = await authFetch(`${API_URL}/api/portal/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: window.location.href }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al abrir el portal');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'Error al abrir el portal de suscripción');
    } finally {
      setLoadingPortal(false);
    }
  };

  // Filter plans: hide "Gratis" for Pro users
  const visiblePlans = isPro ? plans.filter((p) => p.id !== 'free') : plans;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <Compass className="w-7 h-7 text-[#2563EB]" />
              <span className="font-bold text-xl tracking-tight font-['Manrope']">
                Klarvi
              </span>
            </button>
            <div className="flex items-center gap-3">
              {!isAuthenticated ? (
                <>
                  <Button variant="ghost" onClick={() => navigate('/login')} data-testid="pricing-login-btn">
                    Iniciar Sesión
                  </Button>
                  <Button onClick={() => navigate('/register')} className="bg-[#0F172A] hover:bg-[#0F172A]/90" data-testid="pricing-register-btn">
                    Crear Cuenta
                  </Button>
                </>
              ) : (
                <Button onClick={() => navigate('/dashboard')} className="bg-[#0F172A] hover:bg-[#0F172A]/90" data-testid="pricing-dashboard-btn">
                  Ir al Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] font-['Manrope']">
              Planes y Precios
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tus necesidades profesionales.
            </p>
            {isPro && (
              <div className="mt-4 inline-flex items-center gap-2 bg-[#10B981]/10 text-[#10B981] px-4 py-2 rounded-full text-sm font-medium">
                <Crown className="w-4 h-4" />
                Tienes una suscripción Pro activa
              </div>
            )}
          </div>

          <div className={`grid gap-6 items-start ${isPro ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-3'}`}>
            {visiblePlans.map((plan) => {
              const isProPlan = plan.id === 'pro_monthly' || plan.id === 'pro_annual';
              const isCurrentPlan = isPro && isProPlan;

              return (
                <Card
                  key={plan.id}
                  className={`border relative transition-all duration-200 hover:shadow-lg ${
                    plan.popular ? 'border-[#2563EB] shadow-lg scale-[1.02]' : ''
                  }`}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {/* Badge */}
                  {isCurrentPlan ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#10B981] text-white px-3 py-1">
                        <Check className="w-3 h-3 mr-1" />
                        Tu plan actual
                      </Badge>
                    </div>
                  ) : plan.badge ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className={`${plan.popular ? 'bg-[#2563EB]' : 'bg-[#10B981]'} text-white px-3 py-1`}>
                        {plan.popular ? <Crown className="w-3 h-3 mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        {plan.badge}
                      </Badge>
                    </div>
                  ) : null}

                  <CardHeader className="text-center pt-8">
                    <CardTitle className="text-xl font-['Manrope']">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-[#0F172A]">{plan.price}</span>
                      {plan.period && (
                        <span className="text-muted-foreground ml-1">{plan.period}</span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Buttons */}
                    {isPro && isCurrentPlan ? (
                      <Button
                        variant="outline"
                        className="w-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5"
                        disabled
                        data-testid={`pricing-btn-${plan.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Tu plan actual
                      </Button>
                    ) : isPro && isProPlan ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => toast.info('Para cambiar de plan, contacta a soporte.')}
                        data-testid={`pricing-btn-${plan.id}`}
                      >
                        Cambiar a este plan
                      </Button>
                    ) : isAuthenticated && plan.id === 'free' && !isPro ? (
                      <Button
                        variant="outline"
                        className="w-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5"
                        disabled
                        data-testid={`pricing-btn-${plan.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Plan actual
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handlePlanClick(plan.id)}
                        disabled={loadingPlan === plan.id}
                        className={`w-full ${
                          plan.popular
                            ? 'bg-[#2563EB] hover:bg-[#2563EB]/90'
                            : plan.id === 'free'
                            ? 'bg-[#0F172A] hover:bg-[#0F172A]/90'
                            : ''
                        }`}
                        data-testid={`pricing-btn-${plan.id}`}
                      >
                        {loadingPlan === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : plan.id !== 'free' ? (
                          <Crown className="w-4 h-4 mr-2" />
                        ) : null}
                        {plan.cta}
                        {plan.id === 'free' && <ArrowRight className="w-4 h-4 ml-2" />}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Manage subscription link for Pro users */}
          {isPro && (
            <div className="text-center mt-10">
              <Button
                variant="ghost"
                className="text-muted-foreground hover:text-[#0F172A]"
                onClick={handleManageSubscription}
                disabled={loadingPortal}
                data-testid="manage-subscription-btn"
              >
                {loadingPortal ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Settings className="w-4 h-4 mr-2" />
                )}
                Gestionar tu suscripción
              </Button>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 md:px-8 pb-8">
        <ReferralBanner />
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 md:px-8 border-t">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-2">
          <p className="text-xs text-muted-foreground text-center">
            La información proporcionada es solo para fines informativos y educativos.
          </p>
          <a href="/privacidad" className="text-xs text-[#2563EB] hover:underline" data-testid="pricing-footer-privacy-link">
            Aviso de privacidad
          </a>
        </div>
      </footer>
    </div>
  );
}
